import { Directive, Input, TemplateRef, ViewContainerRef, inject, effect, ElementRef } from '@angular/core';
import { AuthService } from '../../core/services/auth.service';
import { PlanService } from '../../core/services/plan.service';

/**
 * Structural directive to conditionally render elements based on permissions
 * 
 * Usage:
 *   <div *hasPermission="'users:create'">Create User Button</div>
 *   <div *hasPermission="['users:create', 'users:update']">Multiple permissions (OR)</div>
 *   <div *hasPermission="'users:create'; requireAll: true">Require all permissions</div>
 */
@Directive({
  selector: '[hasPermission]',
  standalone: true
})
export class HasPermissionDirective {
  private templateRef = inject(TemplateRef<any>);
  private viewContainer = inject(ViewContainerRef);
  private auth = inject(AuthService);
  private plan = inject(PlanService);
  
  private permissions: string[] = [];
  private requireAll = false;
  private hasView = false;

  @Input() set hasPermission(value: string | string[]) {
    this.permissions = Array.isArray(value) ? value : [value];
    this.updateView();
  }

  @Input() set hasPermissionRequireAll(value: boolean) {
    this.requireAll = value;
    this.updateView();
  }

  constructor() {
    // React to permission and plan changes
    effect(() => {
      // Access signals to create dependencies
      this.auth.permissions();
      this.plan.currentPlan();
      this.updateView();
    });
  }

  private updateView() {
    if (this.permissions.length === 0) {
      this.viewContainer.clear();
      this.hasView = false;
      return;
    }

    // TWO-TIER PERMISSION CHECK:
    // TIER 1: Check if resource is available in plan
    const planChecks = this.permissions.map(perm => {
      const [resource] = perm.split(':');
      return this.plan.canAccessResource(resource);
    });

    // TIER 2: Check RBAC permissions
    const userPermissions = this.auth.permissions();
    const rbacChecks = this.permissions.map(perm => userPermissions.includes(perm));

    let hasPermission = false;

    if (this.requireAll) {
      // User must have ALL specified permissions (both plan and RBAC)
      hasPermission = planChecks.every(check => check) && rbacChecks.every(check => check);
    } else {
      // User must have ANY of the specified permissions (both plan and RBAC)
      hasPermission = this.permissions.some((perm, index) => 
        planChecks[index] && rbacChecks[index]
      );
    }

    if (hasPermission && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (!hasPermission && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }
}

/**
 * Directive to hide elements (but keep in DOM) based on permissions
 * 
 * Usage:
 *   <div [hideWithoutPermission]="'users:create'">Hidden if no permission</div>
 */
@Directive({
  selector: '[hideWithoutPermission]',
  standalone: true
})
export class HideWithoutPermissionDirective {
  private elementRef = inject(ElementRef);
  private auth = inject(AuthService);
  
  private permission?: string;

  @Input() set hideWithoutPermission(value: string) {
    this.permission = value;
    this.updateVisibility();
  }

  constructor() {
    effect(() => {
      this.auth.permissions();
      this.updateVisibility();
    });
  }

  private updateVisibility() {
    if (!this.permission) {
      (this.elementRef.nativeElement as HTMLElement).style.display = '';
      return;
    }

    const hasPermission = this.auth.hasPermission()(this.permission);
    (this.elementRef.nativeElement as HTMLElement).style.display = hasPermission ? '' : 'none';
  }
}

/**
 * Directive to disable elements based on permissions
 * 
 * Usage:
 *   <button [disableWithoutPermission]="'users:create'">Create User</button>
 */
@Directive({
  selector: '[disableWithoutPermission]',
  standalone: true
})
export class DisableWithoutPermissionDirective {
  private elementRef = inject(ElementRef);
  private auth = inject(AuthService);
  
  private permission?: string;

  @Input() set disableWithoutPermission(value: string) {
    this.permission = value;
    this.updateDisabled();
  }

  constructor() {
    effect(() => {
      this.auth.permissions();
      this.updateDisabled();
    });
  }

  private updateDisabled() {
    if (!this.permission) {
      (this.elementRef.nativeElement as HTMLElement).removeAttribute('disabled');
      return;
    }

    const hasPermission = this.auth.hasPermission()(this.permission);
    const element = this.elementRef.nativeElement as HTMLElement;
    if (element instanceof HTMLButtonElement || element instanceof HTMLInputElement) {
      element.disabled = !hasPermission;
    } else {
      element.setAttribute('disabled', hasPermission ? '' : 'true');
    }
  }
}

